import { useEffect, useMemo, useState } from 'react';
import {
  FolderTree,
  ImageIcon,
  Loader2,
  Package,
  Search,
  Sparkles,
  Star,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Sidebar from '../../components/Sidebar';
import { getFeaturedContent, saveFeaturedContent } from '../../services/featured';
import { getCategories } from '../../services/categories';
import { getProducts } from '../../services/products';
import type {
  FeaturedContent,
  FeaturedDealProduct,
  FeaturedIcon,
  FeaturedSidebarCategory,
} from '../../types/featured';
import type { Category } from '../../types/category';
import type { ProductCard } from '../../types/product';

type View = 'categories' | 'products';

const emptyFeatured: FeaturedContent = {
  sidebarCategories: [],
  dealProducts: [],
  categorySections: [],
};

function categoryIcon(name: string): FeaturedIcon {
  const lower = name.toLowerCase();
  if (lower.includes('phone') || lower.includes('mobile')) return 'smartphone';
  if (lower.includes('laptop') || lower.includes('pc')) return 'laptop';
  if (lower.includes('audio') || lower.includes('headphone')) return 'headphones';
  if (lower.includes('watch')) return 'watch';
  if (lower.includes('camera')) return 'camera';
  if (lower.includes('gam')) return 'gamepad';
  return 'package';
}

function isCategoryFeatured(category: Category, featured: FeaturedContent): boolean {
  const id = String(category.id);
  return featured.sidebarCategories.some(
    (item) => item.id === id || item.name.toLowerCase() === category.name.toLowerCase(),
  );
}

function isProductFeatured(product: ProductCard, featured: FeaturedContent): boolean {
  const id = String(product.id);
  return featured.dealProducts.some(
    (item) => item.id === id || item.name.toLowerCase() === product.name.toLowerCase(),
  );
}

function toggleCategoryFeatured(
  category: Category,
  featured: FeaturedContent,
): FeaturedContent {
  if (isCategoryFeatured(category, featured)) {
    return {
      ...featured,
      sidebarCategories: featured.sidebarCategories.filter(
        (item) =>
          item.id !== String(category.id) &&
          item.name.toLowerCase() !== category.name.toLowerCase(),
      ),
    };
  }

  const item: FeaturedSidebarCategory = {
    id: String(category.id),
    name: category.name,
    icon: categoryIcon(category.name),
  };

  return {
    ...featured,
    sidebarCategories: [...featured.sidebarCategories, item],
  };
}

function toggleProductFeatured(product: ProductCard, featured: FeaturedContent): FeaturedContent {
  const id = String(product.id);
  if (isProductFeatured(product, featured)) {
    return {
      ...featured,
      dealProducts: featured.dealProducts.filter(
        (item) =>
          item.id !== id && item.name.toLowerCase() !== product.name.toLowerCase(),
      ),
    };
  }

  const item: FeaturedDealProduct = {
    id,
    name: product.name,
    discount: 0,
    image: product.image,
  };

  return {
    ...featured,
    dealProducts: [...featured.dealProducts, item],
  };
}

const ManageFeaturedProductCategory = () => {
  const [featured, setFeatured] = useState<FeaturedContent>(emptyFeatured);
  const [categories, setCategories] = useState<Category[]>([]);
  const [products, setProducts] = useState<ProductCard[]>([]);
  const [view, setView] = useState<View>('categories');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const [featuredData, categoryData, productData] = await Promise.all([
          getFeaturedContent(),
          getCategories(),
          getProducts(),
        ]);
        if (!cancelled) {
          setFeatured(featuredData ?? emptyFeatured);
          setCategories(categoryData);
          setProducts(productData);
        }
      } catch {
        if (!cancelled) toast.error('Failed to load featured content');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const query = search.trim().toLowerCase();

  const filteredCategories = useMemo(() => {
    if (!query) return categories;
    return categories.filter(
      (category) =>
        category.name.toLowerCase().includes(query) ||
        category.slug.toLowerCase().includes(query),
    );
  }, [categories, query]);

  const filteredProducts = useMemo(() => {
    if (!query) return products;
    return products.filter((product) => {
      const categoryName = product.category.name;
      return (
        product.name.toLowerCase().includes(query) ||
        categoryName.toLowerCase().includes(query)
      );
    });
  }, [products, query]);

  const featuredCategoryCount = categories.filter((category) =>
    isCategoryFeatured(category, featured),
  ).length;

  const featuredProductCount = products.filter((product) =>
    isProductFeatured(product, featured),
  ).length;

  const persistFeatured = async (next: FeaturedContent) => {
    setFeatured(next);
    setSaving(true);
    try {
      const saved = await saveFeaturedContent(next);
      setFeatured(saved);
      toast.success('Featured updated');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleCategoryToggle = (category: Category) => {
    void persistFeatured(toggleCategoryFeatured(category, featured));
  };

  const handleProductToggle = (product: ProductCard) => {
    void persistFeatured(toggleProductFeatured(product, featured));
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col w-full">
      <div className="flex flex-1 w-full">
        <Sidebar />
        <main className="flex-1 p-4 md:p-8 w-full max-h-screen overflow-y-auto">
          <div className="mb-6 md:mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 text-xs font-medium text-indigo-700 mb-3">
              <Sparkles className="h-3.5 w-3.5" />
              Homepage highlights
            </div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              Featured
            </h1>
            <p className="mt-1.5 text-sm md:text-base text-slate-500">
              Search categories or products and mark them as featured on your storefront.
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <div className="w-full space-y-4">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search categories or products…"
                  className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-900 shadow-sm transition focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setView('categories')}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                    view === 'categories'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'bg-white text-slate-600 border border-slate-200 hover:border-indigo-200 hover:text-indigo-700'
                  }`}
                >
                  <FolderTree className="h-4 w-4" />
                  Categories
                  <span
                    className={`rounded-md px-1.5 py-0.5 text-[11px] ${
                      view === 'categories' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {featuredCategoryCount} featured
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setView('products')}
                  className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                    view === 'products'
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'bg-white text-slate-600 border border-slate-200 hover:border-indigo-200 hover:text-indigo-700'
                  }`}
                >
                  <Package className="h-4 w-4" />
                  Products
                  <span
                    className={`rounded-md px-1.5 py-0.5 text-[11px] ${
                      view === 'products' ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                    }`}
                  >
                    {featuredProductCount} featured
                  </span>
                </button>
                {saving && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-slate-500">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Saving…
                  </span>
                )}
              </div>

              <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                {view === 'categories' ? (
                  filteredCategories.length === 0 ? (
                    <p className="py-12 text-center text-sm text-slate-500">
                      {query ? 'No categories match your search.' : 'No categories yet. Add some in Products.'}
                    </p>
                  ) : (
                    <ul className="divide-y divide-slate-100">
                      {filteredCategories.map((category) => {
                        const featuredItem = isCategoryFeatured(category, featured);
                        return (
                          <li
                            key={category.id}
                            className="flex items-center gap-3 px-4 py-3 transition hover:bg-slate-50/80"
                          >
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-600">
                              <FolderTree className="h-5 w-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold text-slate-900">
                                {category.name}
                              </p>
                              <p className="truncate text-xs text-slate-500">{category.slug}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleCategoryToggle(category)}
                              disabled={saving}
                              aria-label={featuredItem ? `Remove ${category.name} from featured` : `Mark ${category.name} as featured`}
                              aria-pressed={featuredItem}
                              className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                                featuredItem
                                  ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200 hover:bg-amber-100'
                                  : 'border border-slate-200 text-slate-600 hover:border-amber-200 hover:text-amber-700'
                              }`}
                            >
                              <Star
                                className={`h-4 w-4 ${featuredItem ? 'fill-amber-500 text-amber-500' : ''}`}
                              />
                              {featuredItem ? 'Featured' : 'Mark featured'}
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )
                ) : filteredProducts.length === 0 ? (
                  <p className="py-12 text-center text-sm text-slate-500">
                    {query ? 'No products match your search.' : 'No products yet. Add some in Products.'}
                  </p>
                ) : (
                  <ul className="divide-y divide-slate-100">
                    {filteredProducts.map((product) => {
                      const featuredItem = isProductFeatured(product, featured);
                      const categoryName = product.category.name;
                      return (
                        <li
                          key={product.id}
                          className="flex items-center gap-3 px-4 py-3 transition hover:bg-slate-50/80"
                        >
                          <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-slate-100">
                            {product.image ? (
                              <img
                                src={product.image}
                                alt={product.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="flex h-full w-full items-center justify-center text-slate-400">
                                <ImageIcon className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-semibold text-slate-900">
                              {product.name}
                            </p>
                            <p className="truncate text-xs text-slate-500">
                              {categoryName ?? 'Uncategorized'} · Rs {product.price.toLocaleString()}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleProductToggle(product)}
                            disabled={saving}
                            aria-label={featuredItem ? `Remove ${product.name} from featured` : `Mark ${product.name} as featured`}
                            aria-pressed={featuredItem}
                            className={`inline-flex shrink-0 items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                              featuredItem
                                ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200 hover:bg-amber-100'
                                : 'border border-slate-200 text-slate-600 hover:border-amber-200 hover:text-amber-700'
                            }`}
                          >
                            <Star
                              className={`h-4 w-4 ${featuredItem ? 'fill-amber-500 text-amber-500' : ''}`}
                            />
                            {featuredItem ? 'Featured' : 'Mark featured'}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ManageFeaturedProductCategory;
